import { useState, useRef, useEffect } from 'react';

interface StoreImageCarouselProps {
  images: string[];
  storeName: string;
}

export function StoreImageCarousel({ images, storeName }: StoreImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const touchStartRef = useRef<number>(0);
  const touchEndRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // 기본 이미지 설정 (이미지가 없을 때)
  const displayImages = images.length > 0 && images[0] ? images : [];

  // 이미지 프리로드
  useEffect(() => {
    const preloadImages = async () => {
      if (displayImages.length === 0) {
        setIsLoading(false);
        return;
      }

      const promises = displayImages.map((src, index) => {
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = (error) => {
            console.warn(`Failed to load image ${index + 1}:`, src);
            reject(error);
          };
          img.src = src;
        });
      });

      try {
        await Promise.allSettled(promises);
      } catch (error) {
        // Promise.allSettled를 사용하므로 여기 도달하지 않음
        console.warn('Image loading failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    preloadImages();
  }, [displayImages]);

  // 터치 이벤트 핸들러
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    
    const distance = touchStartRef.current - touchEndRef.current;
    const threshold = 50; // 최소 스와이프 거리

    if (distance > threshold) {
      // 왼쪽으로 스와이프 (다음 이미지)
      goToNext();
    } else if (distance < -threshold) {
      // 오른쪽으로 스와이프 (이전 이미지)
      goToPrevious();
    }
  };

  // 다음 이미지로 이동
  const goToNext = () => {
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
  };

  // 이전 이미지로 이동
  const goToPrevious = () => {
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  // 특정 이미지로 이동
  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  // 자동 슬라이드 (선택사항)
  useEffect(() => {
    if (displayImages.length <= 1) return;
    
    const interval = setInterval(() => {
      goToNext();
    }, 2000); // 2초마다 자동 슬라이드

    return () => clearInterval(interval);
  }, [displayImages.length]);

  return (
    <div className="relative w-full h-80 bg-gray-100 overflow-hidden">
      {/* 이미지가 없을 때 대체 UI */}
      {displayImages.length === 0 ? (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-gray-400 rounded-full flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-600">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21,15 16,10 5,21"/>
              </svg>
            </div>
            <p className="text-gray-600 text-sm font-medium">{storeName}</p>
            <p className="text-gray-500 text-xs mt-1">이미지 준비중</p>
          </div>
        </div>
      ) : (
        <>
          {/* 로딩 상태 */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-pulse bg-gray-300 w-full h-full" />
            </div>
          )}

          {/* 이미지 컨테이너 */}
          <div
            ref={containerRef}
            className="relative w-full h-full"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
        {/* 이미지 슬라이더 */}
        <div
          className="flex w-full h-full transition-transform duration-300 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            width: `${displayImages.length * 100}%`
          }}
        >
          {displayImages.map((image, index) => (
            <div
              key={index}
              className="w-full h-full flex-shrink-0 relative"
              style={{ width: `${100 / displayImages.length}%` }}
            >
              <img
                src={image}
                alt={`${storeName} 이미지 ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // 이미지 로딩 실패 시 스타일로 대체
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                        <div class="text-center">
                          <div class="w-16 h-16 mx-auto mb-3 bg-gray-400 rounded-full flex items-center justify-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-gray-600">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21,15 16,10 5,21"/>
                            </svg>
                          </div>
                          <p class="text-gray-600 text-sm font-medium">${storeName}</p>
                          <p class="text-gray-500 text-xs mt-1">이미지 로딩 실패</p>
                        </div>
                      </div>
                    `;
                  }
                }}
              />
              
              {/* 그라데이션 오버레이 */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
          ))}
        </div>

        {/* 네비게이션 버튼 (이미지가 2개 이상일 때만 표시) */}
        {displayImages.length > 1 && (
          <>
            {/* 이전 버튼 */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md backdrop-blur-sm transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* 다음 버튼 */}
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md backdrop-blur-sm transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 4L10 8L6 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        {/* 인디케이터 (이미지가 2개 이상일 때만 표시) */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        )}

            {/* 이미지 카운터 */}
            {displayImages.length > 1 && (
              <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full text-sm backdrop-blur-sm">
                {currentIndex + 1} / {displayImages.length}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}